import { Injectable, signal, computed } from "@angular/core";

export interface DeviceInfo {
  isZebraDevice: boolean;
  deviceType: "zebra" | "mobile" | "desktop" | "unknown";
  userAgent: string;
  deviceModel?: string;
  capabilities: {
    hasCamera: boolean;
    hasBarcodeScanner: boolean;
    hasPhysicalButtons: boolean;
  };
}

@Injectable({
  providedIn: "root",
})
export class DeviceDetectionService {
  private static readonly ZEBRA_MODEL_CODES = [
    "TC20",
    "TC21",
    "TC26",
    "TC52",
    "TC53",
    "TC57",
    "TC72",
    "TC77",
    "TC83",
    "MC93",
    "MC33",
    "ET50",
    "ET51",
    "ET56",
    "EC30",
    "EC50",
    "EC55",
    "EC60",
    "EC70",
    "EC80",
    "PS20",
    "PS30",
    "PS40",
    "PS60",
    "PS80",
    "WS50",
    "WS60",
    "WS80",
    "ZQ500",
    "ZQ600",
    "ZQ700",
    "ZQ800",
    "ZQ900",
  ] as const;

  private _deviceInfo = signal<DeviceInfo>(this.detectDevice());
  private readonly uaDataEnrichment: Promise<void>;

  readonly deviceInfo = this._deviceInfo.asReadonly();
  readonly isZebraDevice = computed(() => this._deviceInfo().isZebraDevice);
  readonly deviceType = computed(() => this._deviceInfo().deviceType);

  constructor() {
    // Chrome on Android increasingly uses a "reduced" UA string (no device model/manufacturer).
    // Use User-Agent Client Hints (UA-CH) to detect the device model when available.
    this.uaDataEnrichment = this.enrichFromUserAgentData();
  }

  async waitForDeviceDetection(): Promise<void> {
    await this.uaDataEnrichment;
  }

  private detectDevice(): DeviceInfo {
    const userAgent = navigator.userAgent;
    const isZebraDevice = this.isZebraScanner(userAgent);
    const deviceType = this.getDeviceType(userAgent, isZebraDevice);
    const deviceModel = this.extractZebraModel(userAgent);

    return {
      isZebraDevice,
      deviceType,
      userAgent,
      deviceModel,
      capabilities: this.detectCapabilities(userAgent, isZebraDevice),
    };
  }

  private isZebraScanner(userAgent: string): boolean {
    const zebraIndicators = [
      // Zebra device identifiers
      "Zebra",
      ...DeviceDetectionService.ZEBRA_MODEL_CODES,
      // DataWedge identifiers
      "DataWedge",
      "com.symbol",
      "com.zebra",
      // Zebra browser identifiers
      "ZebraBrowser",
      "ZebraWebView",
    ];

    return zebraIndicators.some((indicator) =>
      userAgent.toLowerCase().includes(indicator.toLowerCase()),
    );
  }

  private isZebraModel(model: string): boolean {
    const normalized = model.trim().toUpperCase();
    if (!normalized) return false;

    if (
      DeviceDetectionService.ZEBRA_MODEL_CODES.some((code) =>
        normalized.includes(code),
      )
    ) {
      return true;
    }

    // Fallback for variants like "TC52X", "TC52AX", etc.
    return /^(TC|MC|EC|ET|PS|WS|ZQ)\d+/i.test(normalized);
  }

  private async enrichFromUserAgentData(): Promise<void> {
    const uaData = (navigator as any).userAgentData as  // eslint-disable-line @typescript-eslint/no-explicit-any
      | {
          getHighEntropyValues?: (
            hints: string[],
          ) => Promise<Record<string, unknown>>;
        }
      | undefined;

    if (!uaData?.getHighEntropyValues) return;

    try {
      const values = await uaData.getHighEntropyValues([
        "model",
        "platform",
        "platformVersion",
        "fullVersionList",
      ]);

      const model =
        typeof values["model"] === "string"
          ? (values["model"] as string)
          : undefined;
      if (!model) return;

      const current = this._deviceInfo();
      const zebraByModel = this.isZebraModel(model);
      const isZebraDevice = current.isZebraDevice || zebraByModel;

      if (
        current.deviceModel !== model ||
        current.isZebraDevice !== isZebraDevice
      ) {
        this._deviceInfo.set({
          ...current,
          isZebraDevice,
          deviceType: this.getDeviceType(current.userAgent, isZebraDevice),
          deviceModel: model,
          capabilities: this.detectCapabilities(
            current.userAgent,
            isZebraDevice,
          ),
        });
      }
    } catch {
      // Ignore UA-CH errors (policy/permissions can block high entropy values).
    }
  }

  private getDeviceType(
    userAgent: string,
    isZebraDevice: boolean,
  ): "zebra" | "mobile" | "desktop" | "unknown" {
    if (isZebraDevice) {
      return "zebra";
    }

    // Check for mobile devices
    const mobileIndicators = [
      "Mobile",
      "Android",
      "iPhone",
      "iPad",
      "iPod",
      "BlackBerry",
      "Windows Phone",
    ];

    const isMobile = mobileIndicators.some((indicator) =>
      userAgent.includes(indicator),
    );

    if (isMobile) {
      return "mobile";
    }

    // Check for desktop
    const desktopIndicators = ["Windows NT", "Macintosh", "Linux", "X11"];

    const isDesktop = desktopIndicators.some((indicator) =>
      userAgent.includes(indicator),
    );

    return isDesktop ? "desktop" : "unknown";
  }

  private extractZebraModel(userAgent: string): string | undefined {
    for (const model of DeviceDetectionService.ZEBRA_MODEL_CODES) {
      if (userAgent.includes(model)) {
        return model;
      }
    }

    return undefined;
  }

  private detectCapabilities(
    userAgent: string,
    isZebraDevice: boolean,
  ): {
    hasCamera: boolean;
    hasBarcodeScanner: boolean;
    hasPhysicalButtons: boolean;
  } {
    return {
      hasCamera: this.hasCameraCapability(userAgent, isZebraDevice),
      hasBarcodeScanner: isZebraDevice, // Zebra devices have built-in scanners
      hasPhysicalButtons: isZebraDevice, // Zebra devices have physical scan buttons
    };
  }

  private hasCameraCapability(
    userAgent: string,
    isZebraDevice: boolean,
  ): boolean {
    // Most modern devices have cameras
    const hasCamera =
      "mediaDevices" in navigator && "getUserMedia" in navigator.mediaDevices;

    // Zebra devices typically have cameras
    if (isZebraDevice) {
      return true;
    }

    // Check for camera support in user agent
    const cameraIndicators = ["camera", "Camera", "webcam"];
    const hasCameraInUA = cameraIndicators.some((indicator) =>
      userAgent.includes(indicator),
    );

    return hasCamera || hasCameraInUA;
  }

  /**
   * Refresh device detection (useful if device capabilities change)
   */
  refreshDetection(): void {
    this._deviceInfo.set(this.detectDevice());
    void this.enrichFromUserAgentData();
  }
}
